import { useCallback, useEffect, useState } from "react";
import useLiveApi from "./use-live-api";
import { Vehicle } from "../interfaces";
import { usePagination } from "./usePagination";

interface VehiclesApi {
  data: Vehicle[];
  loading: boolean;
  error: boolean;
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const fetchItems = async (
  apiGatewayFetch: (
    url: string,
    method: string,
    body?: string
  ) => Promise<Response>,
  accountId: string,
  registration?: string,
  paginationToken?: string
) => {
  let url = `/account/${accountId}/vehicles`;
  if (registration) url += `?registration=${registration}`;
  if (paginationToken) url += `?token=${paginationToken}`;
  const response = await apiGatewayFetch(url, "get");
  if (response.status !== 200) {
    throw new Error(response.status.toString());
  }
  return await response.json();
};

export const useVehiclesApi = (
  accountId: string,
  registration?: string
): VehiclesApi => {
  const { apiGatewayFetch } = useLiveApi();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [data, setData] = useState<Vehicle[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([""]); // page 1 has no token
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

  const loadPage = useCallback(
    async (paginationToken?: string) => {
      setLoading(true);
      setError(false);
      try {
        const data = await fetchItems(
          apiGatewayFetch,
          accountId,
          registration,
          paginationToken
        );
        setData(data.items as Vehicle[]);
        setNextToken(data.paginationToken ?? null);
      } catch (e) {
        setError(e.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    },
    [apiGatewayFetch, accountId, registration]
  );

  const { nextPage, prevPage, hasNextPage, hasPrevPage } = usePagination({
    loadPage,
    nextToken,
    currentPageIndex,
    pageTokens,
    setPageTokens,
    setCurrentPageIndex,
  });

  useEffect(() => {
    setPageTokens([""]);
    setCurrentPageIndex(0);
    loadPage(); // Initial load without token
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this once
  }, [accountId, registration]);

  return { data, nextPage, prevPage, hasNextPage, hasPrevPage, loading, error };
};
